import {
  action, computed, flow, observable, makeObservable,
} from 'mobx';
import isEmpty from 'lodash/isEmpty';

import BaseStore from './BaseStore';
import ExamCentersSearchAPI from '../api/exam_centers_search';
import ExamCentersSearchAdsAPI from '../api/exam_centers_search_ads';
import { ExamCentersSortTypes } from '../components/ExamCentersSearch/Filters/constants';
import { ExamCentersTypes } from '../constants/ExamCenters';

const hasAllExamCenters = (selectedExamCentersFilters) => selectedExamCentersFilters.length === Object.keys(ExamCentersTypes).length - 1;

const addFilter = (selectedFilters, filterName) => [...selectedFilters, filterName];
const removeFilter = (selectedFilters, filterName) => selectedFilters.filter((f) => f !== filterName);
const toggleFilter = (selectedFilters, filterName) => selectedFilters.includes(filterName) ? removeFilter(selectedFilters, filterName) : addFilter(selectedFilters, filterName);

class ExamCentersSearchStore extends BaseStore {
  examCentersData = {};
  isDataLoading = false;
  isAdsLoading = false;

  selectedExamCentersFilters = [ExamCentersTypes.ALL];
  selectedSort = ExamCentersSortTypes.DISTANCE;

  constructor() {
    super();

    makeObservable(this, {
      examCentersData: observable,
      isDataLoading: observable,
      isAdsLoading: observable,
      selectedExamCentersFilters: observable,
      selectedSort: observable,
      filterByExamCenters: action.bound,
      sortedExamCenters: computed,
    });
  }

  getExamCentersByCoordinates = flow(function* (latitude, longitude, radius) {
    this.isDataLoading = true;
    try {
      const { data } = yield ExamCentersSearchAPI.getExamCentersByCoordinates(latitude, longitude, radius);
      this.examCentersData = data;
    } catch (e) {
      console.error(e);
    } finally {
      this.isDataLoading = false;
    }
  });

  getExamCentersByDepartmentAndCity = flow(function* (department, city) {
    this.isDataLoading = true;
    try {
      const { data } = yield ExamCentersSearchAPI.getExamCentersByDepartmentAndCity(department, city);
      this.examCentersData = data;
    } catch (e) {
      console.error(e);
    } finally {
      this.isDataLoading = false;
    }
  });

  getAdsByCoordinates = flow(function* (latitude, longitude, radius) {
    this.isAdsLoading = true;
    try {
      const { data } = yield ExamCentersSearchAdsAPI.getAdsByCoordinates(latitude, longitude, radius);
      this.ads = data;
    } catch (e) {
      console.error(e);
    } finally {
      this.isAdsLoading = false;
    }
  });

  getAdsByDepartmentAndCity = flow(function* (department, city) {
    this.isAdsLoading = true;
    try {
      const { data } = yield ExamCentersSearchAdsAPI.getAdsByDepartmentAndCity(department, city);
      this.ads = data;
    } catch (e) {
      console.error(e);
    } finally {
      this.isAdsLoading = false;
    }
  });

  filterByExamCenters(examCenterName) {
    if (examCenterName === ExamCentersTypes.ALL && this.selectedExamCentersFilters.includes(ExamCentersTypes.ALL)) {
      return;
    }

    if (examCenterName === ExamCentersTypes.ALL) {
      this.selectedExamCentersFilters = [ExamCentersTypes.ALL];
      return;
    }

    let nextSelectedExamCentersFilters = toggleFilter(this.selectedExamCentersFilters, examCenterName);

    if (nextSelectedExamCentersFilters.includes(ExamCentersTypes.ALL)) {
      nextSelectedExamCentersFilters = removeFilter(nextSelectedExamCentersFilters, ExamCentersTypes.ALL);
    }

    if (isEmpty(nextSelectedExamCentersFilters) || hasAllExamCenters(nextSelectedExamCentersFilters)) {
      nextSelectedExamCentersFilters = [ExamCentersTypes.ALL];
    }

    this.selectedExamCentersFilters = nextSelectedExamCentersFilters;
  }

  sortByNextSession = (feature1, feature2) => {
    let nextSession1 = feature1.properties.next_session;
    let nextSession2 = feature2.properties.next_session;
    if (!nextSession1 || !nextSession2) {
      if (nextSession1) {
        return -1;
      }
      if (nextSession2) {
        return 1;
      }
      return 0;
    }
    nextSession1 = Date.parse(new Date(nextSession1));
    nextSession2 = Date.parse(new Date(nextSession2));
    return nextSession1 - nextSession2;
  };

  sortByBookingAvailability = (feature1, feature2) => {
    const props1 = feature1.properties;
    const props2 = feature2.properties;
    const isBookableCenter1 = !!(props1.is_codengo || props1.is_la_poste || props1.is_dekra || props1.is_sgs);
    const isBookableCenter2 = !!(props2.is_codengo || props2.is_la_poste || props2.is_dekra || props2.is_sgs);

    return isBookableCenter2 - isBookableCenter1;
  };

  get sortedExamCenters() {
    if (this.examCentersData.features) {
      const clonedExamCentersFeatures = [...this.examCentersData.features];
      if (this.selectedSort === ExamCentersSortTypes.DISTANCE) {
        clonedExamCentersFeatures.sort(this.sortByBookingAvailability);
      } else if (this.selectedSort === ExamCentersSortTypes.SESSION) {
        clonedExamCentersFeatures.sort(this.sortByBookingAvailability).sort(this.sortByNextSession);
      }

      if (this.selectedExamCentersFilters[0] !== ExamCentersTypes.ALL) {
        return clonedExamCentersFeatures.filter((el) => {
          const { properties } = el;
          let { type } = properties;
          type = type.replace(/ /g, '').toUpperCase();
          if (this.selectedExamCentersFilters.includes(ExamCentersTypes[type])) {
            return el;
          }
          return null;
        });
      }
      return clonedExamCentersFeatures;
    }

    return [];
  }
}

export default new ExamCentersSearchStore();
